import { Box } from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeadingText, ParagraphText } from "../../../../Libraries";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { formatNum } from "../../../../oscar-pos-core/constants";
import {
  ArrowDown,
  ArrowDownLeftIcon,
  ArrowDownRightIcon,
  ArrowUp,
  CopyIcon,
  RefreshNewIcon,
  RupinNewLogo,
  ThreeDotsIcon,
  ThreeDotVertical,
} from "../../../../assets/icons/icon";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import RupinLogo from "../../../../assets/icons/Logo Rupin.svg";
import styles from "./WalletBalanceSection.module.css";
import { PopoverMenu } from "../../../common";
import { useWalletContext } from "../../../../context/WalletContext";
import { EMIONBOARDINGSTATUS } from "../../../../constants";

const WalletBalanceSection = ({ balance, moneyIn, moneyOut, raastDetails }) => {
  const [I18n] = useContext(LanguageContext);
  const { walletData, handleGetWalletBalance, fetchAccountBalanceLoading } =
    useWalletContext();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [copyKey, setCopyKey] = useState("");
  const [isBalanceHidden, setIsBalanceHidden] = useState(false);

  const accountNumber = raastDetails?.raast_details?.account_number || "";
  const iban = raastDetails?.raast_details?.iban || accountNumber;
  const accountTitle = raastDetails?.raast_details?.name || "";
  const phoneNumber = user?.phone_number || "";

  const isEMIVerified =
    walletData?.account_info?.emi_onboarding_status ===
    EMIONBOARDINGSTATUS.VERIFIED;

  const handleCopy = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyKey(key);
      triggerLogEvent(analytics, "w_home_copy_detail", { type: key });
      setTimeout(() => setCopyKey(""), 1500);
    } catch (error) {
      console.error(error);
    }
  };

  const handleViewStatement = () => {
    triggerLogEvent(analytics, "w_view_statement");
    navigate("/UCash/AllTransaction");
  };

  const handleToggleBalance = () => {
    triggerLogEvent(analytics, "w_toggle_balance");
    setIsBalanceHidden((prev) => !prev);
  };

  const handleRefreshBalance = (e) => {
    e.stopPropagation();
    if (fetchAccountBalanceLoading) return;
    triggerLogEvent(analytics, "w_home_refresh_balance");
    handleGetWalletBalance(navigate);
  };

  const Actions = [
    {
      label: (
        <Box display={"flex"} alignItems={"center"}>
          <ParagraphText className="textCapitalizeFirstLetter">
            {I18n.account_details}
          </ParagraphText>
        </Box>
      ),
      action: (e) => {
        e.stopPropagation();
        triggerLogEvent(analytics, "w_view_account_details");
        navigate("/Ucash/AccountDetail");
      },
    },
    {
      label: (
        <Box display={"flex"} alignItems={"center"}>
          <ParagraphText className="textCapitalizeFirstLetter">
            {I18n.account_limit}
          </ParagraphText>
        </Box>
      ),
      action: (e) => {
        e.stopPropagation();
        triggerLogEvent(analytics, "w_view_account_limit");
        navigate("/UCash/AccountLimit");
      },
    },
    {
      label: (
        <Box display={"flex"} alignItems={"center"}>
          <ParagraphText className="textCapitalizeFirstLetter">
            {I18n.bank_statement}
          </ParagraphText>
        </Box>
      ),
      action: (e) => {
        e.stopPropagation();
        triggerLogEvent(analytics, "w_view_bank_statement");
        navigate("/Ucash/ViewBankStatement");
      },
    },
    {
      label: (
        <Box display={"flex"} alignItems={"center"}>
          <ParagraphText >
            {I18n.share_qr}
          </ParagraphText>
        </Box>
      ),
      action: (e) => {
        e.stopPropagation();
        // Separate from the dashboard quick action's w_share_qr so the two entry points can be told apart
        triggerLogEvent(analytics, "w_card_share_qr");
        navigate("/UCash/ShowQR");
      },
    },
  ];

  const HIDDEN_AMOUNT_MASK = "●".repeat(6);

  return (
    <Box className={styles.section}>
      <Box className={styles.balancePanel}>
        <ParagraphText className={styles.balanceLabel}>
          {I18n.balance}
        </ParagraphText>
        <Box className={styles.balanceAmountRow}>
          <HeadingText
            tag="h2"
            className={styles.balanceAmount}
            onClick={handleToggleBalance}
          >
            {isBalanceHidden ? (
              <span className={styles.balanceHiddenDots}>●●●●●●</span>
            ) : (
              <>Rs. {balance}</>
            )}
          </HeadingText>
          <button
            id="wallet_balance_refresh_btn"
            type="button"
            className={styles.refreshButton}
            onClick={handleRefreshBalance}
            aria-label={I18n.refresh}
            disabled={fetchAccountBalanceLoading}
          >
            <RefreshNewIcon
              className={fetchAccountBalanceLoading ? styles.refreshSpin : "marginLeft5"}
              height={18}
              width={18}
            />
          </button>
        </Box>
        <button
          id="wallet_balance_view_statement_btn"
          type="button"
          className={styles.viewStatementLink}
          onClick={handleViewStatement}
        >
          {I18n.view_transactions}
        </button>

        <Box className={styles.monthlyStats}>
          <Box className={styles.monthlyStatItem}>
            <span className={styles.incomingIcon}>
              <ArrowDownLeftIcon />
            </span>
            <ParagraphText className={styles.monthlyStatText}>
              {I18n.incoming_this_month}:
              <span className={styles.monthlyStatAmount}>
                Rs.{" "}
                {isBalanceHidden ? (
                  <span className={styles.hiddenAmount}>
                    {HIDDEN_AMOUNT_MASK}
                  </span>
                ) : (
                  formatNum(Math.floor(moneyIn || 0))
                )}
              </span>
            </ParagraphText>
          </Box>
          <Box className={styles.monthlyStatSeparator}></Box>
          <Box className={styles.monthlyStatItem}>
            <ParagraphText className={styles.monthlyStatText}>
              {I18n.outgoing_this_month}:
              <span className={styles.monthlyStatAmount}>
                Rs.{" "}
                {isBalanceHidden ? (
                  <span className={styles.hiddenAmount}>
                    {HIDDEN_AMOUNT_MASK}
                  </span>
                ) : (
                  formatNum(Math.floor(Math.abs(moneyOut || 0)))
                )}
              </span>
            </ParagraphText>
            <span className={styles.outgoingIcon}>
              <ArrowDownRightIcon />
            </span>
          </Box>
        </Box>
      </Box>

      {isEMIVerified ? (
        <Box className={styles.accountCard}>
          <Box className={styles.accountCardTop}>
            <RupinNewLogo />
            <PopoverMenu
              id="expence"
              menuOptions={Actions}
              popoverButton={<ThreeDotsIcon />}
              popoverButtonClassName={styles.accountCardMenu}
              openPopoverClassName={styles.accountCardMenuOpen}
              popoverButtonRestProps={{
                "aria-label": "Menu",
                id: "wallet_balance_menu_btn",
              }}
            />
          </Box>

          <Box className={styles.accountCardRow}>
            <ParagraphText className={styles.accountCardValue}>
              {iban}
            </ParagraphText>
            <button
              id="wallet_balance_copy_iban_btn"
              type="button"
              className={styles.copyButton}
              onClick={() => handleCopy(iban, "iban")}
              aria-label={I18n.copy}
            >
              <CopyIcon width={14} height={14} />
              {copyKey === "iban" ? I18n.copied : ""}
            </button>
          </Box>

          <Box className={styles.accountCardRow}>
            <ParagraphText className={styles.accountCardValue}>
              {phoneNumber}
            </ParagraphText>
            <button
              id="wallet_balance_copy_phone_btn"
              type="button"
              className={styles.copyButton}
              onClick={() => handleCopy(phoneNumber, "phone")}
              aria-label={I18n.copy}
            >
              <CopyIcon width={14} height={14} />
              {copyKey === "phone" ? I18n.copied : ""}
            </button>
          </Box>

          <ParagraphText className={styles.accountCardName}>
            {accountTitle}
          </ParagraphText>
        </Box>
      ) : null}
    </Box>
  );
};

export default WalletBalanceSection;
